/**
 * RouteStopsPanel - Right sidebar for route info, stops, and available orders
 * Supports cross-panel drag-and-drop between Available Orders and Run Stops
 */
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { offlineRunPlan } from '@/utils/offlineRunPlan';
import { runSendHold, RUN_SEND_HOLD_MESSAGE } from '@/utils/runSendRecovery';
import { Button } from '@/components/ui/button';
import { Navigation, Unlock, CheckCircle, Eye, EyeOff, User, Route, Clock, ChevronUp, ChevronDown, X, Bell, Archive, Play, Printer, Flag, Pencil, AlertTriangle } from 'lucide-react';
import usePreparedPrint from '@/hooks/usePreparedPrint';
import FuelCostEstimator from '@/components/FuelCostEstimator';
import StatusBadge from '@/components/StatusBadge';
import GoogleMapsLinks from '@/components/GoogleMapsLinks';
import {
  DndContext,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDndContext,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EmptyState from '@/components/EmptyState';
import DirectionsPanel from '@/components/DirectionsPanel';
import { getOrderNumberSuffix } from '@/utils/markerUtils';
import { classifyManualOrderPlacement } from '@/utils/offlineRunIdentity';

const STOPS_ID = 'stops';
const AVAILABLE_ID = 'available';

// Draggable stop item (in the run)
const DraggableStopItem = ({ stop, isHovered, onHover, onRemove, readOnly = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.order_id,
    data: { container: STOPS_ID },
    disabled: readOnly
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const orderSuffix = getOrderNumberSuffix(stop.order?.internal_order_number);
  const orderMissingOffline = Boolean(stop.order_missing_offline);
  const recipientLabel = stop.order?.recipient_name || (
    orderMissingOffline ? 'Order unavailable offline' : 'Unknown'
  );
  const addressLabel = stop.order?.recipient_street_address || (
    orderMissingOffline ? 'Order details are not cached on this device' : 'No address'
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded-xl border transition-all select-none ${
        readOnly ? '' : 'cursor-grab active:cursor-grabbing touch-none'
      } ${
        isDragging
          ? 'bg-primary/10 border-blue-400 shadow-soft-lg'
          : isHovered
            ? 'bg-primary/5 border-primary/30 shadow-soft-md'
            : 'bg-white hover:bg-stone-50'
      }`}
      onMouseEnter={() => onHover(stop.order_id)}
      onMouseLeave={() => onHover(null)}
      data-testid={`stop-row-${stop.order_id}`}
      {...(readOnly ? {} : { ...attributes, ...listeners })}
    >
      <div className={`w-8 h-10 rounded flex flex-col items-center justify-center flex-shrink-0 transition-colors ${
        isHovered || isDragging ? 'bg-red-600' : 'bg-slate-700'
      }`}>
        <span className="text-xs font-bold leading-none text-green-400">{stop.sequence}</span>
        <div className="w-full h-px bg-white/20 my-0.5"></div>
        <span className="text-[10px] leading-none text-white/90">#{orderSuffix}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-1">
          {stop.order?.dispatch_note?.trim() && (
            <Flag className="h-3.5 w-3.5 text-red-600 fill-red-600 flex-shrink-0" data-testid={`stop-flag-${stop.order_id}`} />
          )}
          <span className="truncate">{recipientLabel}</span>
          {orderMissingOffline && (
            <span className="rounded-full border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
              Offline cache miss
            </span>
          )}
        </div>
        <div className="text-xs text-stone-500 truncate">
          {addressLabel}
          {stop.order?.recipient_street_address_2 && `, ${stop.order.recipient_street_address_2}`}
        </div>
      </div>
      {!readOnly && (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onRemove(stop.order_id); }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <X className="h-4 w-4 text-red-500" />
        </Button>
      )}
    </div>
  );
};

// Draggable available order item
const hasOfflineMapPosition = (order) => {
  return classifyManualOrderPlacement(order).isPlaced;
};

const DraggableAvailableItem = ({ order, isHovered, onHover, onHoverClear, readOnly = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: order.id,
    data: { container: AVAILABLE_ID },
    disabled: readOnly
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const orderSuffix = getOrderNumberSuffix(order.internal_order_number);
  const localOrder = order?.desktop_sync?.origin === 'local';
  const placed = hasOfflineMapPosition(order);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded-xl border transition-all select-none touch-none text-xs ${
        readOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
      } ${
        isDragging
          ? 'bg-purple-50 border-purple-300 shadow-lg'
          : isHovered
            ? 'bg-amber-50 border-amber-300 shadow-soft-sm'
            : 'bg-white/80 hover:bg-stone-50 border-stone-200/50'
      }`}
      onMouseEnter={() => onHover(order.id)}
      onMouseLeave={() => onHoverClear()}
      data-testid={`available-order-${order.id}`}
      {...(readOnly ? {} : { ...attributes, ...listeners })}
    >
      <div className={`w-6 h-6 rounded-xl flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${
        isHovered ? 'bg-red-500' : 'bg-amber-500'
      }`}>
        #{orderSuffix}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate flex items-center gap-1">
          {order.dispatch_note?.trim() && (
            <Flag className="h-3 w-3 text-red-600 fill-red-600 flex-shrink-0" data-testid={`available-flag-${order.id}`} />
          )}
          <span className="truncate">{order.recipient_name || 'Unknown'}</span>
        </div>
        {localOrder && (
          <div className="mt-1 flex flex-wrap gap-1">
            <span
              className="inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 border border-orange-200"
              data-testid={`available-offline-badge-${order.id}`}
            >
              Offline
            </span>
            <span
              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${
                placed
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-stone-100 text-stone-600 border-stone-200'
              }`}
              data-testid={`available-placement-badge-${order.id}`}
            >
              {placed ? 'Placed' : 'Unplaced'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const MissingMapOrderItem = ({ order }) => {
  const orderSuffix = getOrderNumberSuffix(order.internal_order_number);
  const localOrder = order?.desktop_sync?.origin === 'local';
  const addressParts = [
    order.recipient_street_address,
    order.recipient_street_address_2,
    [order.recipient_city, order.recipient_state, order.recipient_zip_code]
      .filter(Boolean)
      .join(' '),
  ].filter(Boolean);

  return (
    <div
      className="rounded-lg border border-orange-200 bg-white/80 px-2.5 py-2 text-xs"
      data-testid={`not-on-map-order-${order.id}`}
    >
      <div className="flex items-center gap-1.5 font-medium text-stone-800">
        <span className="text-orange-700">#{orderSuffix}</span>
        <span className="min-w-0 flex-1 truncate">{order.recipient_name || 'Unknown recipient'}</span>
        {localOrder && (
          <span
            className="rounded-full border border-orange-200 bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700"
            data-testid={`not-on-map-offline-badge-${order.id}`}
          >
            Offline
          </span>
        )}
      </div>
      <div className="mt-0.5 truncate text-stone-500">
        {addressParts.length > 0 ? addressParts.join(', ') : 'No delivery address'}
      </div>
    </div>
  );
};

// Drag overlay preview
const DragPreview = ({ item, type }) => {
  if (!item) return null;
  if (type === 'stop') {
    const orderSuffix = getOrderNumberSuffix(item.order?.internal_order_number);
    return (
      <div className="flex items-center gap-2 p-2 rounded-xl bg-white shadow-xl border border-blue-300 w-56 opacity-90">
        <div className="w-8 h-10 rounded flex flex-col items-center justify-center bg-slate-700 flex-shrink-0">
          <span className="text-xs font-bold leading-none text-green-400">{item.sequence}</span>
          <div className="w-full h-px bg-white/20 my-0.5"></div>
          <span className="text-[10px] leading-none text-white/90">#{orderSuffix}</span>
        </div>
        <div className="text-sm font-medium truncate">{item.order?.recipient_name || '?'}</div>
      </div>
    );
  }
  const orderSuffix = getOrderNumberSuffix(item.internal_order_number);
  return (
    <div className="flex items-center gap-2 p-2 rounded-xl bg-white shadow-xl border border-amber-300 w-56 opacity-90">
      <div className="w-6 h-6 rounded-xl bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
        #{orderSuffix}
      </div>
      <div className="text-sm font-medium truncate">{item.recipient_name || '?'}</div>
    </div>
  );
};

// Droppable zone wrapper
//
// The highlight deliberately does not use this droppable's own `isOver`. Every
// stop inside a zone is itself a drop target, because stops are reorderable, and
// dnd-kit resolves exactly one target at a time. Dragging over an existing stop
// therefore made that stop the target and switched the zone's `isOver` off,
// removing the highlight while the drop was still perfectly valid --
// `handleDragEnd` accepts it by reading the same `container` this consults.
//
// So the zone asks "is the current target me, or anything belonging to me?"
// rather than "am I the target?", and the highlight matches what a drop will
// actually do. Fixes backlog item UX-03.
const DroppableZone = ({ id, children, className = '' }) => {
  const { setNodeRef } = useDroppable({ id, data: { container: id } });
  const { over } = useDndContext();
  const isOverZone = Boolean(
    over && (over.id === id || over.data?.current?.container === id),
  );
  return (
    <div
      ref={setNodeRef}
      data-testid={`droppable-${id}`}
      data-drop-active={isOverZone ? 'true' : 'false'}
      className={`${className} ${isOverZone ? 'ring-2 ring-purple-300 ring-inset' : ''}`}
    >
      {children}
    </div>
  );
};


function formatStartedAt(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatRunCacheFreshness(iso) {
  if (!iso) return 'Run data has not been refreshed on this device';
  const timestamp = new Date(iso);
  if (Number.isNaN(timestamp.getTime())) {
    return 'Run data has not been refreshed on this device';
  }
  return `Run data last updated ${timestamp.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

const RouteStopsPanel = ({
  selectedRun,
  runCommands,
  captureSelection,
  isSelectionCurrent,
  routeInfo,
  directionSteps,
  loadingRoute = false,
  routeError = null,
  routeWarnings = [],
  onRetryRoute,
  showDirections,
  setShowDirections,
  hideCommittedOrders,
  setHideCommittedOrders,
  hoveredStopId,
  setHoveredStopId,
  hoveredAvailableOrderId,
  setHoveredAvailableOrderId,
  onCommit,
  onUncommit,
  onRenameRun,
  onDiscardOfflineProposal,
  onAddToRun,
  onRemoveFromRun,
  onDragEnd,
  availableOrders,
  ordersNotShownOnMap = [],
  filteredOrdersCount,
  ordersLoaded = true,
  onAssignDriverClick,
  onCloseRun,
  onStartRun,
  onUnstartRun,
  onNotifyDriver,
  isMinimized = false,
  onToggleMinimize,
  // Driver mode - hides admin controls and disables editing
  driverMode = false,
  offline = false,
  runCacheLastUpdated = null,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const printSelectedRun = usePreparedPrint({
    kind: 'run', record: selectedRun,
    canPrint: !driverMode && Boolean(selectedRun?.populated_stops?.length),
  });
  const [activeId, setActiveId] = useState(null);

  // Inline run-name editing (draft/committed only; closed runs are frozen)
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const cancelRenameRef = useRef(false);
  const renameTargetRef = useRef(null);
  const renamePlan = useRef(null);
  const renameHandler = useRef(null);
  const renameOpening = useRef(0);
  const renameSelection = useRef(null);
  const renameSending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const selectedRunRef = useRef(selectedRun);
  selectedRunRef.current = selectedRun;
  const sendHeld = !!runSendHold(selectedRun);
  const runBlocked = sendHeld || Boolean(selectedRun && runCommands?.blockedRun(selectedRun.id));

  const assignedDriver = selectedRun?.driver ? selectedRun.driver : null;
  const isDraft = !selectedRun || selectedRun.status === 'draft';
  const isClosed = selectedRun?.status === 'completed';
  const hasDeliveredStop = selectedRun?.populated_stops?.some(
    (stop) => stop.order?.status === 'delivered',
  ) || false;
  const selectedRunIsOfflineDraft = selectedRun?.origin === 'local_desktop' || selectedRun?.is_offline_draft === true;
  const canEditOfflineRun = offline && selectedRun?.status === 'draft';
  const cachedProposalActive = offline && !selectedRunIsOfflineDraft && selectedRun?.has_offline_proposal;
  const driverAssignmentLocked = runBlocked || (offline && !canEditOfflineRun) || !isDraft || hasDeliveredStop;
  const canRename = !runBlocked && !driverMode && !isClosed && !!selectedRun && (!offline || canEditOfflineRun);

  const startRename = () => {
    if (!canRename) return;
    setNameDraft(selectedRun.name);
    renameTargetRef.current = selectedRun.id;
    renamePlan.current = offline ? offlineRunPlan(selectedRun) : null;
    renameHandler.current = onRenameRun;
    renameSelection.current = captureSelection?.();
    renameOpening.current += 1;
    cancelRenameRef.current = false;
    setIsEditingName(true);
  };

  const commitRename = async () => {
    if (!canRename || renameTargetRef.current !== selectedRun?.id) return;
    if (renameSending.current || (isSelectionCurrent && !isSelectionCurrent(renameSelection.current))) return;
    // Esc sets this so the blur that follows doesn't also save
    if (cancelRenameRef.current) {
      cancelRenameRef.current = false;
      return;
    }
    if (!onRenameRun) return;
    const opening = renameOpening.current;
    const targetId = selectedRun.id;
    const selection = renameSelection.current;
    renameSending.current = true;
    try {
      const accepted = renamePlan.current ? await renameHandler.current(targetId, nameDraft, renamePlan.current)
        : await renameHandler.current(targetId, nameDraft);
      if (accepted === true && mounted.current && renameOpening.current === opening
        && selectedRunRef.current?.id === targetId && (!isSelectionCurrent || isSelectionCurrent(selection))) {
        setIsEditingName(false);
      }
    } catch {
      // The command owner reports the outcome; retain the unsent editor here.
    } finally { renameSending.current = false; }
  };

  const cancelRename = () => {
    cancelRenameRef.current = true;
    renameOpening.current += 1;
    setIsEditingName(false);
  };
  const isReadOnly = runBlocked || driverMode || !isDraft || (offline && !canEditOfflineRun);

  // Check if all stops are delivered (for Close Run button)
  const allDelivered = useMemo(() => {
    const stops = selectedRun?.populated_stops;
    return stops?.length > 0 && stops.every(s => s.order?.status === 'delivered');
  }, [selectedRun]);

  const stopIds = useMemo(() =>
    selectedRun?.populated_stops?.map(s => s.order_id) || [],
    [selectedRun]
  );
  const availableIds = useMemo(() => availableOrders.map(o => o.id), [availableOrders]);

  // Find active item for overlay
  const activeStop = useMemo(() =>
    activeId ? selectedRun?.populated_stops?.find(s => s.order_id === activeId) : null,
    [activeId, selectedRun]
  );
  const activeAvailable = useMemo(() =>
    activeId ? availableOrders.find(o => o.id === activeId) : null,
    [activeId, availableOrders]
  );

  const findContainer = (id) => {
    if (stopIds.includes(id)) return STOPS_ID;
    if (availableIds.includes(id)) return AVAILABLE_ID;
    return null;
  };

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    // No drag operations in driver mode or when not draft
    if (!over || isReadOnly || runCommands?.blockedOrder(active.id)) return;

    const sourceContainer = active.data?.current?.container || findContainer(active.id);
    const overContainer = over.data?.current?.container || findContainer(over.id);

    // Reorder within stops
    if (sourceContainer === STOPS_ID && overContainer === STOPS_ID && active.id !== over.id) {
      onDragEnd(event);
      return;
    }

    // Available → Stops: add to run
    if (sourceContainer === AVAILABLE_ID && (overContainer === STOPS_ID || over.id === STOPS_ID)) {
      onAddToRun(active.id);
      return;
    }

    // Stops → Available: remove from run
    if (sourceContainer === STOPS_ID && (overContainer === AVAILABLE_ID || over.id === AVAILABLE_ID)) {
      onRemoveFromRun(active.id);
      return;
    }
  };

  return (
    <>
      {selectedRun && !offline && (loadingRoute || routeError) && (
        <div className="absolute top-3 left-1/2 z-20 w-[calc(100%-24px)] max-w-sm -translate-x-1/2 bg-white/95 rounded-xl px-3 py-2 shadow-soft-lg text-sm" role="status" data-testid="route-request-status">
          {loadingRoute ? 'Calculating route…' : (routeError?.message || routeError)}
          {!loadingRoute && routeError && routeError.retryable !== false && (
            <Button size="sm" variant="outline" className="ml-2" onClick={onRetryRoute} data-testid="retry-route">
              Try again
            </Button>
          )}
        </div>
      )}
      <div data-testid="route-stops-sidebar" className={`absolute bottom-3 lg:top-3 right-3 left-3 lg:left-auto lg:bottom-3 w-auto lg:w-72 flex flex-col-reverse lg:flex-col gap-3 z-10 lg:h-[calc(100%-24px)] transition-all duration-200 ${
        isMinimized ? 'max-h-[52px]' : ''
      }`}>
        
        {selectedRun && !offline && routeWarnings.length > 0 && (
          <div className="bg-white/95 rounded-xl px-3 py-2 text-sm" data-testid="route-warnings">
            {routeWarnings.map((warning, index) => <p key={index}>{warning}</p>)}
          </div>
        )}
        {/* Route Info Badge */}
        {selectedRun && routeInfo && (
          <div className="flex gap-2 justify-end flex-shrink-0 flex-wrap">
            <span className="flex items-center gap-1.5 bg-primary/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-xl text-sm font-medium shadow-soft-lg">
              <Route className="h-4 w-4" />
              {routeInfo.distance} mi
            </span>
            <span className="flex items-center gap-1.5 bg-green-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-xl text-sm font-medium shadow-soft-lg">
              <Clock className="h-4 w-4" />
              {routeInfo.duration} min
            </span>
            {!offline && (
              <>
                <FuelCostEstimator distanceMiles={parseFloat(routeInfo.distance)} />
                <Button
                  size="sm"
                  variant={showDirections ? "default" : "outline"}
                  onClick={() => setShowDirections(!showDirections)}
                  className={`h-8 px-3 shadow-soft-lg ${showDirections ? 'bg-primary text-white hover:bg-primary/80' : 'bg-white/90 backdrop-blur-sm hover:bg-stone-100'}`}
                  data-testid="toggle-directions-btn"
                >
                  <Navigation className="h-4 w-4 mr-1.5" />
                  <span className="text-sm">Directions</span>
                </Button>
              </>
            )}
          </div>
        )}

        {/* Minimized bar */}
        {isMinimized && (
          <div
            className="bg-white/95 backdrop-blur-sm rounded-xl shadow-soft-lg border border-stone-200/50 px-4 py-2.5 flex items-center justify-between cursor-pointer"
            onClick={onToggleMinimize}
            data-testid="minimized-right-panel"
            title="Expand route panels"
          >
            <div className="flex items-center gap-3 text-sm">
              <span className="font-medium text-stone-800">{selectedRun ? selectedRun.name : 'Select Run'}</span>
              <span className="text-stone-500 text-xs">{selectedRun?.populated_stops?.length || 0} stops</span>
              <span className="text-amber-600 text-xs">{availableOrders.length} available</span>
              {ordersNotShownOnMap.length > 0 && (
                <span className="text-orange-700 text-xs" data-testid="minimized-not-on-map-count">
                  {ordersNotShownOnMap.length} not on map
                </span>
              )}
            </div>
            <ChevronUp className="h-4 w-4 text-stone-600" />
          </div>
        )}

        {/* Full panels */}
        {!isMinimized && (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <>
              {/* Stops Panel */}
              <div className={`bg-white/95 backdrop-blur-sm rounded-xl shadow-soft-lg border border-stone-200/50 flex flex-col overflow-hidden ${
                driverMode 
                  ? 'max-h-[60vh] lg:max-h-none lg:flex-1' 
                  : 'max-h-[35vh] lg:max-h-[60%] flex-shrink-0'
              }`}>
                <div className="px-4 py-3 border-b border-stone-200/50 bg-stone-50/50">
                  <button
                    onClick={onToggleMinimize}
                    className="absolute top-2 right-2 p-1 hover:bg-stone-200/50 rounded transition-colors z-10"
                    data-testid="minimize-right-panel"
                    title="Minimize route panels"
                    aria-label="Minimize route panels"
                  >
                    <ChevronDown className="h-4 w-4 text-stone-600" />
                  </button>

                  {!selectedRun ? (
                    <h3 className="font-medium text-sm text-stone-500" data-testid="run-panel-title">Select a Run</h3>
                  ) : (
                    <>
                      {/* Row 1: Status badge + stop count */}
                      <div className="flex items-center justify-between mb-1.5" data-testid="run-panel-eyebrow">
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${
                          isClosed ? 'text-stone-400' 
                          : selectedRun.status === 'committed' ? 'text-green-700'
                          : 'text-stone-500'
                        }`} data-testid="run-panel-status-badge" title={sendHeld ? RUN_SEND_HOLD_MESSAGE : undefined}>
                          {sendHeld ? 'Send held — check Review' : isClosed ? 'Closed' : selectedRun.status === 'committed' ? 'Committed' : 'Draft'}
                          {selectedRunIsOfflineDraft && (
                            <span className="ml-1.5 text-orange-600">Offline</span>
                          )}
                          {cachedProposalActive && (
                            <span className="ml-1.5 text-orange-600">Offline changes</span>
                          )}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400" data-testid="run-panel-stop-count">
                          {selectedRun.populated_stops?.length || 0} stops
                        </span>
                      </div>

                      {/* Row 2: Run name + push bell */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0 mr-2">
                          {isEditingName && renameTargetRef.current === selectedRun.id ? (
                            <input
                              autoFocus
                              value={nameDraft}
                              disabled={sendHeld}
                              maxLength={100}
                              onChange={(e) => { renameOpening.current += 1; setNameDraft(e.target.value); }}
                              onBlur={commitRename}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                                else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                              }}
                              className="w-full text-lg font-bold text-stone-900 tracking-tight leading-none bg-white border border-primary/40 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
                              data-testid="run-name-input"
                            />
                          ) : (
                            <h3
                              className={`text-lg font-bold text-stone-900 tracking-tight leading-none flex items-center gap-1 group ${canRename ? 'cursor-text' : ''}`}
                              data-testid="run-panel-title"
                              onClick={canRename ? startRename : undefined}
                              title={canRename ? 'Click to rename' : undefined}
                            >
                              <span className="truncate">{selectedRun.name}</span>
                              {canRename && (
                                <Pencil className="h-3 w-3 text-stone-300 group-hover:text-primary flex-shrink-0" data-testid="rename-run-btn" />
                              )}
                            </h3>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!driverMode && selectedRun.populated_stops?.length > 0 && (
                            <button
                              onClick={() => printSelectedRun()}
                              className="flex items-center gap-0.5 px-1.5 py-1 rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                              title="Print run sheet (paper backup for the driver)"
                              data-testid="print-run-sheet-btn"
                            >
                              <Printer className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {!driverMode && assignedDriver && (
                            <button
                              onClick={onNotifyDriver}
                              disabled={offline || runBlocked}
                              className="flex items-center gap-0.5 px-1.5 py-1 rounded-full hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={offline ? 'Driver notifications are unavailable offline' : 'Send push notification to driver'}
                              data-testid="notify-driver-btn"
                            >
                              <span className="text-[8px] font-bold uppercase tracking-wider">Push</span>
                              <Bell className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Row 3: Driver assignment card */}
                      {!driverMode && (
                        <button
                          onClick={onAssignDriverClick}
                          disabled={driverAssignmentLocked}
                          className="flex items-center gap-2 w-full p-2 bg-white border border-stone-100 rounded-lg hover:border-stone-200 transition-colors mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="assign-driver-btn"
                          title={offline && canEditOfflineRun
                            ? 'Record a local driver proposal; the driver will not be notified'
                            : offline
                              ? 'Cached cloud runs are read-only offline'
                            : !isDraft
                              ? 'Uncommit this run before changing its driver'
                              : hasDeliveredStop
                                ? 'Driver assignment is locked because this run has a delivered order'
                                : undefined}
                        >
                          {assignedDriver ? (
                            <>
                              <div className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm" style={{ backgroundColor: assignedDriver.color || '#6B7280' }} />
                              <span className="text-sm font-medium text-stone-700 truncate" data-testid="driver-assignment-name">
                                {assignedDriver.name}
                                {offline && (selectedRunIsOfflineDraft || selectedRun?.offline_proposal?.changed_sections?.includes('driver')) && <span className="ml-1 text-[10px] text-orange-600">Proposed</span>}
                              </span>
                            </>
                          ) : (
                            <>
                              <User className="h-3.5 w-3.5 text-stone-400" />
                              <span className="text-sm text-stone-400">Assign driver</span>
                            </>
                          )}
                        </button>
                      )}

                      {/* Row 4: Action buttons — stacked vertically */}
                      {!driverMode && (selectedRun.populated_stops?.length > 0 || (offline && isDraft)) && !isClosed && (
                        <div className="flex flex-col gap-1.5">
                          {offline && !canEditOfflineRun && (
                            <p className="rounded-lg bg-orange-50 border border-orange-200 px-2 py-1.5 text-[11px] text-orange-800" data-testid="offline-run-readonly-note">
                              Run changes are unavailable offline.
                            </p>
                          )}
                          {offline && selectedRunIsOfflineDraft && canEditOfflineRun && (
                            <p className="rounded-lg bg-orange-50 border border-orange-200 px-2 py-1.5 text-[11px] text-orange-800" data-testid="offline-draft-run-note">
                              Local offline draft. Add, remove, and reorder stops here; sync happens later.
                            </p>
                          )}
                          {offline && !selectedRunIsOfflineDraft && canEditOfflineRun && (
                            <div className="rounded-lg bg-orange-50 border border-orange-200 px-2 py-1.5 text-[11px] text-orange-800" data-testid="cached-run-proposal-note">
                              Cached cloud draft. Offline changes are provisional and must be reviewed after reconnecting.
                              {cachedProposalActive && (
                                <button
                                  type="button"
                                  className="ml-1 font-semibold underline"
                                  disabled={runBlocked}
                                  onClick={() => onDiscardOfflineProposal?.(selectedRun.id, offlineRunPlan(selectedRun))}
                                  data-testid="discard-cached-run-proposal"
                                >
                                  Discard offline changes
                                </button>
                              )}
                            </div>
                          )}
                          {selectedRun.status === 'committed' ? (
                            <>
                              {/* Start Run / Started indicator */}
                              {selectedRun.started_at ? (
                                <div className="flex items-center justify-between gap-2 px-1" data-testid="run-started-indicator">
                                  <span className="flex items-center gap-1.5 text-[11px] font-medium text-green-700">
                                    <Play className="h-3 w-3 fill-green-700" />
                                    Started {formatStartedAt(selectedRun.started_at)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={onUnstartRun}
                                    disabled={offline || runBlocked}
                                    className="text-[11px] text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="unstart-run-btn"
                                  >
                                    Unstart
                                  </button>
                                </div>
                              ) : (
                                <Button size="sm" onClick={onStartRun} disabled={offline || runBlocked}
                                  className="w-full h-8 text-xs bg-green-600 hover:bg-green-700" data-testid="start-run-btn">
                                  <Play className="h-3.5 w-3.5 mr-1.5 fill-white" />
                                  Start Run
                                </Button>
                              )}
                              {allDelivered && (
                                <Button size="sm" onClick={onCloseRun} disabled={offline || runBlocked}
                                  className="w-full h-8 text-xs bg-stone-800 hover:bg-stone-900" data-testid="close-run-btn">
                                  <Archive className="h-3.5 w-3.5 mr-1.5" />
                                  Close Run
                                </Button>
                              )}
                            </>
                          ) : selectedRun.status === 'draft' ? (
                            <Button
                              size="sm"
                              onClick={onCommit}
                              disabled={runBlocked || offline || !selectedRun.user_id}
                              className={`w-full h-8 text-xs ${selectedRun.user_id
                                ? 'bg-green-700 hover:bg-green-800'
                                : 'bg-stone-300 cursor-not-allowed'}`}
                              title={offline ? 'Run changes are unavailable offline' : !selectedRun.user_id ? 'Assign a driver before committing' : ''}
                              data-testid="commit-run-btn"
                            >
                              <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                              Commit Run
                            </Button>
                          ) : null}
                        </div>
                      )}
                      {/* Commitment can outlive the last stop; keep its reversal reachable. */}
                      {!driverMode && selectedRun.status === 'committed' && (
                        <Button size="sm" variant="ghost" onClick={onUncommit} disabled={offline || runBlocked}
                          className="w-full h-8 text-xs text-amber-700 hover:bg-amber-50" data-testid="uncommit-run-btn">
                          <Unlock className="h-3.5 w-3.5 mr-1.5" />
                          Uncommit
                        </Button>
                      )}
                      {isClosed && !driverMode && selectedRun.started_at && (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-1.5 px-1 text-[11px] text-stone-400" data-testid="run-started-closed-indicator">
                            <Play className="h-3 w-3 fill-stone-400" />
                            Started {formatStartedAt(selectedRun.started_at)}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                <DroppableZone id={STOPS_ID} className="flex-1 overflow-y-auto p-3">
                  {!selectedRun ? (
                    <EmptyState>{driverMode ? 'Select a run to view stops' : 'Select a run from the left panel'}</EmptyState>
                  ) : !selectedRun.populated_stops || selectedRun.populated_stops.length === 0 ? (
                    <EmptyState>{driverMode ? 'No stops in this run' : 'Drag orders here to add stops'}</EmptyState>
                  ) : (
                    <div className="space-y-1.5">
                      {/* Google Maps Navigation Links - shown in driver mode */}
                      {driverMode && selectedRun.populated_stops.length > 0 && (
                        <div className="mb-3 p-3 rounded-xl bg-blue-50/80 border border-blue-200/50">
                          <GoogleMapsLinks stops={selectedRun.populated_stops} />
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 p-2 rounded-xl bg-green-50/80 border border-green-200/50 text-xs">
                        <div className="w-6 h-6 rounded-xl bg-green-600 text-white flex items-center justify-center text-[10px] font-bold">S</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-green-800 truncate">Start: Las Vegas Bouquet</div>
                        </div>
                      </div>
                      
                      <SortableContext items={stopIds} strategy={verticalListSortingStrategy} disabled={isReadOnly}>
                        {selectedRun.populated_stops.map((stop) => (
                          <DraggableStopItem
                            key={stop.order_id}
                            stop={stop}
                            isHovered={hoveredStopId === stop.order_id}
                            onHover={setHoveredStopId}
                            onRemove={onRemoveFromRun}
                            readOnly={isReadOnly || runCommands?.blockedOrder(stop.order_id)}
                          />
                        ))}
                      </SortableContext>
                    </div>
                  )}
                </DroppableZone>
              </div>

              {/* Available Orders Panel - hidden in driver mode */}
              {!driverMode && (
                <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-soft-lg border border-stone-200/50 flex flex-col overflow-hidden max-h-[25vh] lg:max-h-none lg:flex-1 lg:min-h-[33%]">
                  {ordersNotShownOnMap.length > 0 && (
                    <div
                      className="border-b border-orange-200 bg-orange-50/90 px-3 py-2.5"
                      data-testid="orders-not-on-map-notice"
                      role="status"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-700" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm text-orange-900">
                            {ordersNotShownOnMap.length} order{ordersNotShownOnMap.length !== 1 ? 's are' : ' is'} not shown on the map
                          </p>
                          <p className="mt-0.5 text-[11px] leading-snug text-orange-800">
                            Waiting for design or map placement. These orders are not ready to assign.
                          </p>
                          <div className="mt-2 max-h-32 space-y-1.5 overflow-y-auto pr-1">
                            {ordersNotShownOnMap.map((order) => (
                              <MissingMapOrderItem key={order.id} order={order} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="px-4 py-2.5 border-b border-stone-200/50 bg-amber-50/50 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-sm text-stone-800 flex items-center gap-2">
                          Available Orders
                          <StatusBadge status="designed" size="sm" showDot={true} />
                        </h3>
                        {ordersLoaded && (
                          <p className="text-xs text-stone-500">{availableOrders.length} order{availableOrders.length !== 1 ? 's' : ''} to assign</p>
                        )}
                        {offline && (
                          <p
                            className="mt-0.5 text-[10px] text-stone-500"
                            data-testid="run-cache-freshness"
                          >
                            {formatRunCacheFreshness(runCacheLastUpdated)}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setHideCommittedOrders(!hideCommittedOrders)}
                        disabled={offline}
                        className={`h-7 text-xs px-2 ${hideCommittedOrders ? 'bg-stone-200' : ''}`}
                        title={offline ? 'Map marker controls are unavailable offline' : hideCommittedOrders ? 'Show committed markers on map' : 'Hide committed markers on map'}
                        data-testid="toggle-hide-committed"
                      >
                        {offline ? (
                          <><EyeOff className="h-3 w-3 mr-1" /> Map: offline</>
                        ) : hideCommittedOrders ? (
                          <><EyeOff className="h-3 w-3 mr-1" /> Map: hiding</>
                        ) : (
                          <><Eye className="h-3 w-3 mr-1" /> Map: show all</>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <DroppableZone id={AVAILABLE_ID} className="overflow-y-auto p-3">
                    {!ordersLoaded ? null : availableOrders.length === 0 ? (
                      <EmptyState>
                        {filteredOrdersCount === 0
                          ? 'No orders for this date'
                          : 'All designed orders have been added to runs'}
                      </EmptyState>
                    ) : (
                      <div className="space-y-1.5">
                        <SortableContext items={availableIds} strategy={verticalListSortingStrategy}>
                          {availableOrders.map((order) => (
                            <DraggableAvailableItem
                              key={order.id}
                              order={order}
                              isHovered={hoveredAvailableOrderId === order.id}
                              onHover={setHoveredAvailableOrderId}
                              onHoverClear={() => setHoveredAvailableOrderId(null)}
                              readOnly={isReadOnly || runCommands?.blockedOrder(order.id)}
                            />
                          ))}
                        </SortableContext>
                      </div>
                    )}
                  </DroppableZone>
                </div>
              )}
            </>

            {/* Drag overlay */}
            <DragOverlay>
              {activeStop ? (
                <DragPreview item={activeStop} type="stop" />
              ) : activeAvailable ? (
                <DragPreview item={activeAvailable} type="available" />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Directions panel */}
      {showDirections && selectedRun && directionSteps.length > 0 && (
        <div className="absolute top-3 right-[312px] z-10 hidden lg:block">
          <DirectionsPanel
            directionSteps={directionSteps}
            routeInfo={routeInfo}
            runName={selectedRun.name}
            onClose={() => setShowDirections(false)}
          />
        </div>
      )}
    </>
  );
};

export default RouteStopsPanel;
